import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../zustand/loginStore";
import { createAuthenticatedAxiosInstance, fetchCategories } from "api/auth";
import CustomNavbar from "components/users/CustomNavbar";
import SearchAndFilterBar from "components/general/SearchAndFilterBar";
import CustomSelect from "components/general/CustomSelect";
import CustomPagination from "components/general/CustomPagination";
import LoadingScreen from "components/general/LoadingScreen";
import logo from "assets/logo.png";
import { Category } from "interfaces/ICategory";
import CustomBackButton from "components/general/CustomBackButton";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SearchTypes } from "types/SearchTypes";
import { OrderTypes } from "types/OrdersTypes";

const Categories: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    created_from: "",
    created_to: "",
  });
  const [sortConfig, setSortConfig] = useState({
    sort_by: "name",
    order: "asc" as OrderTypes,
  });
  const [searchType, setSearchType] = useState<SearchTypes>("text");
  const [searchOptions, setSearchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [perPage, setPerPage] = useState(10);

  /**
   * Per page options for pagination
   * @constant {Array<{value: string, label: string}>}
   */
  const perPageOptions = [
    { value: "10", label: "10 por página" },
    { value: "25", label: "25 por página" },
    { value: "50", label: "50 por página" },
    { value: "100", label: "100 por página" },
  ];

  /**
   * Per page filter state for CustomSelect
   */
  const [perPageSelect, setPerPageSelect] = useState<{ value: string; label: string } | null>({ value: "10", label: "10 por página" });

  /**
   * Effect hook to fetch and load categories data
   * @effect
   * @dependencies [currentPage, searchTerm, filters.status, sortConfig]
   *
   * The effect:
   * 1. Makes API call to fetch categories with current filters and pagination
   * 2. Updates categories state with response data
   * 3. Updates total pages for pagination
   * 4. Manages loading state
   * 5. Handles error cases
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchCategories(axiosInstance, {
          page: currentPage,
          per_page: perPage,
          name: searchTerm,
          status: filters.status,
          sort_by: sortConfig.sort_by,
          order: sortConfig.order,
        });
        setCategories(response.categories);
        setTotalPages(response.total_pages);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [currentPage, searchTerm, filters.status, sortConfig, perPage]);

  /**
   * Handles click event on table rows
   * @function handleRowClick
   * @param {number} categoryId - ID of the clicked category
   *
   * Navigates to the detailed view of the selected category
   */
  const handleRowClick = (categoryId: number) => {
    navigate(`/inventories/categories/${categoryId}`);
  };

  /**
   * Handles search term changes
   * @function handleSearch
   * @param {string} term - The search term entered by user
   *
   * Updates search term state and resets pagination to first page
   */
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  /**
   * Handles filter change
   * @function handleFilterChange
   * @param {{ value: string; label: string } | null} newValue - The new filter value
   */
  const handleFilterChange = async (
    newValue: { value: string; label: string } | null
  ) => {
    setFilterBy(newValue);
    setCurrentPage(1);

    if (newValue && newValue.value === "status") {
      setSearchType("select");
      setSearchOptions([
        { value: "1", label: "Activo" },
        { value: "0", label: "Inactivo" },
      ]);
    } else {
      setSearchTerm("");
      setSearchType("text");
      setSearchOptions([]);
    }
  };

  /**
   * Handles per page filter change
   * @function handlePerPageChange
   * @param {{ value: string; label: string } | null} newValue - The new per page value
   */
  const handlePerPageChange = (newValue: { value: string; label: string } | null) => {
    setPerPageSelect(newValue);
    setPerPage(parseInt(newValue?.value || "10"));
    setCurrentPage(1);
  };

  /**
   * Handles column sorting
   * @function handleSort
   * @param {string} sortBy - Column field to sort by
   *
   * Updates sort configuration:
   * - Toggles between ascending and descending order if same column
   * - Sets ascending order for new column
   * - Resets to first page
   */
  const handleSort = (sortBy: string) => {
    setSortConfig((prev) => ({
      sort_by: sortBy,
      order: prev.sort_by === sortBy && prev.order === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  /**
   * Handles pagination changes
   * @function handlePageChange
   * @param {number} page - Target page number
   *
   * Updates current page state for pagination
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Handles navigation to add category page
   * @function handleAddCategory
   *
   * Navigates user to category creation form
   */
  const handleAddCategory = () => {
    navigate("/inventories/categories/add");
  };

  /**
   * Filter options for the product table
   * @constant {Array<{value: string, label: string}>}
   */
  const filterOptions = [
    { value: "name", label: "Nombre" },
    { value: "active", label: "Activadas", status: "1" },
    { value: "inactive", label: "Desactivadas", status: "0" },
  ];

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/dashboard#inventarios")} />

      <Container className="px-4 px-md-5">
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>Categorías</h1>
          </Col>
        </Row>

        <SearchAndFilterBar
          searchType={searchType}
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          searchOptions={searchOptions}
          filterBy={filterBy}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
          isSearchDisabled={!filterBy}
        />

        {/* Per page selector - aligned below the filter */}
        <Row className="mb-3">
          <Col xs={6}></Col>
          <Col xs={6} className="d-flex justify-content-end">
            <div className="d-flex align-items-center w-75">
              <InputGroup>
                <div className="filter-select-container">
                  <CustomSelect
                    options={perPageOptions}
                    value={perPageSelect}
                    onChange={handlePerPageChange}
                    placeholder="Elementos por página..."
                    isFilteredIconVisible={false}
                  />
                </div>
              </InputGroup>
            </div>
          </Col>
        </Row>

        {isLoading ? (
          <LoadingScreen loadingText="Cargando categorías..." />
        ) : (
          <>
            <div className="table-container mb-5">
              <Table striped bordered hover className="border-radius-10">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="bg-custom-yellow text-center">Imagen</th>
                    <th
                      className="bg-custom-yellow text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("name")}
                    >
                      Nombre
                      {sortConfig.sort_by === "name" && (
                        <FontAwesomeIcon
                          icon={
                            sortConfig.order === "asc" ? faSortUp : faSortDown
                          }
                          className="ms-2"
                        />
                      )}
                      {sortConfig.sort_by !== "name" && (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      )}
                    </th>
                    <th
                      className="bg-custom-yellow text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("status")}
                    >
                      Estado
                      {sortConfig.sort_by === "status" && (
                        <FontAwesomeIcon
                          icon={
                            sortConfig.order === "asc" ? faSortUp : faSortDown
                          }
                          className="ms-2"
                        />
                      )}
                      {sortConfig.sort_by !== "status" && (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category, index) => (
                    <tr
                      key={category.id}
                      className={`custom-row ${
                        index % 2 === 0 ? "odd" : "even"
                      }`}
                      onClick={() => handleRowClick(category.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        <img
                          src={`${category.images[0]}?t=${Date.now()}`}
                          alt={category.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <div
                          style={{
                            maxHeight: "3em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {category.name}
                        </div>
                      </td>

                      <td className="text-center">
                        <span
                          className={`badge ${
                            category.status === 1
                              ? "badge-active"
                              : "badge-inactive"
                          }`}
                        >
                          {category.status === 1 ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Row className="justify-content-center mb-3">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className=" fw-bold"
                  onClick={handleAddCategory}
                >
                  Agregar categoría
                </Button>
              </Col>
            </Row>
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalResults={categories?.length}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Categories;
